import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress
} from '@material-ui/core';

import { useTimeout } from '../../common/timeout.hook';
import { FetchError } from '../../common/api/api';
import { useFileUploader } from './components/DocumentFolderUpload';
import { CreateFileVersionDto } from './Document.interface';

export type DocumentUploadRef = (file: File) => void;

export interface DocumentUploadProps {
  isLoading?: boolean;
  loadingMessage?: React.ReactNode;
  innerRef?: React.MutableRefObject<DocumentUploadRef | undefined>;
  onError?: (error: FetchError) => void;
  onSuccess?: (fileVersion: CreateFileVersionDto) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = props => {
  const {
    innerRef,
    isLoading = false,
    loadingMessage,
    onSuccess,
    onError
  } = props;
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const [fileState, setFile] = useState<File>();
  const uploader = useFileUploader();
  const [debounceClear] = useTimeout();

  const uploadItem = useMemo(() => {
    return fileState && uploader.find(fileState);
  }, [fileState, uploader]);

  const {
    file,
    status,
    progress = 0,
    presignedUrl,
    error: uploadError
  } = uploadItem || {};
  const open = !!file && status !== 'cancelled';
  const isUploaded = status === 'uploaded';
  const isUploading = status
    ? ['loading', 'uploading'].includes(status)
    : false;

  const clearState = useCallback(() => {
    uploader.clear();
    setFile(undefined);
  }, [uploader]);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!innerRef) {
      return;
    }
    innerRef.current = file => {
      clearState();
      setFile(file);
      uploader.upload([file]);
    };
  }, [innerRef, uploader, clearState]);

  // debounce clear to avoid dialog closing and reopening behavior
  useEffect(() => {
    if (isUploaded) {
      return debounceClear(() => clearState(), 200);
    }
  }, [isUploaded, clearState, debounceClear]);

  // only call onSuccess once uploaded
  useEffect(() => {
    if (isUploaded && file && presignedUrl) {
      onSuccessRef.current?.({
        size: file.size,
        mimeType: file.type,
        storageKey: presignedUrl.storageKey
      });
    }
  }, [file, isUploaded, presignedUrl]);

  useEffect(() => {
    if (uploadError) {
      clearState();
      onErrorRef.current?.(uploadError);
    }
  }, [uploadError, clearState]);

  return (
    <Dialog open={open || isLoading}>
      <DialogTitle>Document Upload</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {loadingMessage ||
            (file ? `Uploading ${file.name || 'file'}...` : 'Loading...')}
        </DialogContentText>
        <LinearProgress
          value={progress}
          variant={isLoading ? 'indeterminate' : 'determinate'}
        />
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isLoading}
          onClick={() => file && uploader.cancel(file)}
        >
          {isUploading ? 'Cancel' : 'Dismiss'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUpload;
